import React, { useState, useEffect } from 'react'
import { Steps, Modal, Form, Input } from 'antd'
import type { WOStatusType } from '@/hooks/useSystemTables'
import axiosInstance from '@/lib/axios'

interface Transition {
  to_state: string
  allowed: boolean
  reason_required: boolean
  explanation: string
}

interface PhaseProgressBarProps {
  currentStato: string
  statusTypes: WOStatusType[]
  // Opzionali — se omessi la barra è sola lettura
  workOrderId?: number
  interventionsCount?: number
  hasDescrizione?: boolean
  onStateChange?: (newState: string) => void
}

const PhaseProgressBar: React.FC<PhaseProgressBarProps> = ({
  currentStato,
  statusTypes,
  workOrderId,
  interventionsCount = 0,
  hasDescrizione = false,
  onStateChange,
}) => {
  const [transitions, setTransitions] = useState<Transition[]>([])
  const [pendingToState, setPendingToState] = useState<string | undefined>()
  const [reasonModalOpen, setReasonModalOpen] = useState(false)
  const [transitionInProgress, setTransitionInProgress] = useState(false)
  const [form] = Form.useForm()

  const interactive = !!workOrderId

  useEffect(() => {
    if (!interactive) return
    const params = new URLSearchParams()
    params.append('interventions_count', String(interventionsCount))
    params.append('has_descrizione', hasDescrizione ? 'true' : 'false')
    axiosInstance
      .get(`/work-orders/${workOrderId}/available-transitions?${params.toString()}`)
      .then(res => setTransitions(res.data.available_transitions || []))
      .catch(() => setTransitions([]))
  }, [workOrderId, currentStato, interventionsCount, hasDescrizione, interactive])

  const phases = statusTypes
    .filter(s => s.nome !== 'sospesa' && s.nome !== 'annullata')
    .sort((a, b) => a.ordine - b.ordine)

  const isSospesa = currentStato === 'sospesa'
  const effectiveStato = isSospesa ? 'in lavorazione' : currentStato
  const currentIdx = phases.findIndex(p => p.nome === effectiveStato)
  const safeIdx = currentIdx === -1 ? 0 : currentIdx

  const allowedSet = new Set(transitions.filter(t => t.allowed).map(t => t.to_state))

  const executeTransition = async (toState: string, reason?: string) => {
    setTransitionInProgress(true)
    try {
      await axiosInstance.post(`/work-orders/${workOrderId}/transition/${toState}`, {
        reason,
        interventions_count: interventionsCount,
        has_descrizione: hasDescrizione,
      })
      setPendingToState(undefined)
      onStateChange?.(toState)
    } catch (err: any) {
      Modal.error({
        title: 'Errore transizione',
        content: err?.response?.data?.detail || 'Impossibile eseguire la transizione.',
      })
    } finally {
      setTransitionInProgress(false)
      setReasonModalOpen(false)
      form.resetFields()
    }
  }

  const handleStepClick = (idx: number) => {
    if (!interactive) return
    const phase = phases[idx]
    if (!phase || idx === safeIdx) return
    if (!allowedSet.has(phase.nome)) return

    const isBackward = idx < safeIdx
    const transitionDef = transitions.find(t => t.to_state === phase.nome)

    if (transitionDef?.reason_required) {
      setPendingToState(phase.nome)
      setReasonModalOpen(true)
    } else if (isBackward) {
      Modal.confirm({
        title: 'Arretramento stato',
        content: `Stai arretrando la scheda a "${phase.nome}". Sei sicuro?`,
        okText: 'Sì, arretra',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => executeTransition(phase.nome),
      })
    } else {
      executeTransition(phase.nome)
    }
  }

  const items = phases.map((phase, idx) => {
    let status: 'finish' | 'process' | 'wait' | 'error' = 'wait'
    if (idx < safeIdx) status = 'finish'
    else if (idx === safeIdx) status = isSospesa ? 'error' : 'process'

    const isClickable = interactive && allowedSet.has(phase.nome) && idx !== safeIdx

    return {
      key: phase.nome,
      title: phase.nome,
      status,
      disabled: interactive && !isClickable && idx !== safeIdx,
      className: isClickable ? 'ppb-step-clickable' : '',
    }
  })

  const pendingPhase = phases.find(p => p.nome === pendingToState)

  return (
    <>
      <style>{`
        .ppb-step-clickable { cursor: pointer; }
        .ppb-step-clickable:hover .ant-steps-item-icon {
          border-color: #1890ff !important;
          background: #e6f7ff !important;
        }
        .ppb-step-clickable:hover .ant-steps-item-icon .ant-steps-icon {
          color: #1890ff !important;
        }
        .ppb-step-clickable:hover .ant-steps-item-title {
          color: #1890ff !important;
        }
      `}</style>

      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <Steps
          current={safeIdx}
          status={isSospesa ? 'error' : 'process'}
          items={items}
          style={{ minWidth: 700 }}
          size="small"
          onChange={handleStepClick}
        />
      </div>

      <Modal
        title={`Motivazione — ${pendingPhase?.nome ?? ''}`}
        open={reasonModalOpen}
        onCancel={() => { setReasonModalOpen(false); setPendingToState(undefined); form.resetFields() }}
        onOk={() => form.submit()}
        confirmLoading={transitionInProgress}
        okText="Conferma"
        cancelText="Annulla"
        width="min(440px, 95vw)"
      >
        <Form
          form={form}
          onFinish={values => pendingToState && executeTransition(pendingToState, values.reason)}
        >
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Inserisci una motivazione' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Inserisci la motivazione per questa transizione…"
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default PhaseProgressBar
